import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthContext } from '../../../shared/context/auth-context';

export const useAccountDetailsState = ({ profile, saveChanges }) => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [modalTextContent] = useState<{ title: string; text: string[] }>();
  const [profileItem, setProfileItem] = useState<any>(profile);
  const [activeDept, setActiveDept] = useState<string | undefined>(undefined);
  const [activeSubDept, setActiveSubDept] = useState<string | undefined>(undefined);
  const [subDeptsArr, setSubDeptsArr] = useState<string[]>([]);
  const [activeRegion, setActiveRegion] = useState<string | undefined>(undefined);
  const [regionArr, setRegionArr] = useState<string[]>([]);
  const [regionGroupArr, setRegionGroupArr] = useState<string[]>([]);
  const [regionGroup, setRegionGroup] = useState<string | undefined>(undefined);
  const [regionsOptions, setRegionsOptions] = useState<string[]>([]);
  const [allRegions, setAllRegions] = useState<any[]>([]);
  const [showGreenCheck, setShowGreenCheck] = useState<boolean>(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);

  // Error handling state
  const [isAfterSubmit, setIsAfterSubmit] = useState<boolean>(false);
  const [errorSummary, setErrorSummary] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<{
    nameRequired: boolean;
    emailRequired: boolean;
    emailInvalid: boolean;
    regionRequired: boolean;
    departmentRequired: boolean;
    subDepartmentRequired: boolean;
  }>({
    nameRequired: false,
    emailRequired: false,
    emailInvalid: false,
    regionRequired: false,
    departmentRequired: false,
    subDepartmentRequired: false,
  });

  const modalClickHandler = useCallback(() => {
    setModalIsOpen(false);
  }, []);

  // Removed openModal function as it's no longer used with new error handling

  const showGreenCheckOverlay = () => {
    setShowGreenCheck(true);
    setTimeout(() => {
      setShowGreenCheck(false);
    }, 2000);
  };

  const getFieldError = (fieldName: keyof typeof fieldErrors): string | null => {
    if (!isAfterSubmit || !fieldErrors[fieldName]) return null;

    switch (fieldName) {
      case 'nameRequired':
        return t('S054');
      case 'emailRequired':
      case 'emailInvalid':
        return t('S055');
      case 'regionRequired':
        return t('R057');
      case 'departmentRequired':
        return t('R058');
      case 'subDepartmentRequired':
        return t('R059');
      default:
        return null;
    }
  };

  const hasFieldError = (fieldName: keyof typeof fieldErrors): boolean => {
    return isAfterSubmit && fieldErrors[fieldName];
  };

  const clearFieldError = (fieldName: keyof typeof fieldErrors) => {
    setFieldErrors((prev) => ({
      ...prev,
      [fieldName]: false,
    }));
  };

  useEffect(() => {
    let userRegion;
    // exclude users without client regions or those with automatic region overwrite
    if (
      profile.regions?.length > 0 &&
      !(profile.access === '2' && profile?.fandf_region_ovewrite === profile.user?.region)
    ) {
      // existing user region
      userRegion = profile.regions.find((obj) => obj._id === profile.region);
      if (userRegion) {
        setActiveRegion(userRegion.verbose_name);
        if (userRegion.region_group) {
          setRegionGroup(userRegion.region_group);
        }
      }

      // all regions for this client
      const regions = [];

      profile.regions.forEach((region) => regions.push(region.verbose_name));
      setRegionArr(regions);
      setAllRegions(profile.regions);

      if (profile.regions[0].region_group) {
        const groupArr = [];
        profile.regions.forEach((region) => {
          if (!groupArr.includes(region.region_group)) {
            groupArr.push(region.region_group);
          }
        });
        let filteredOptionsArr = [];
        if (userRegion && userRegion.region_group) {
          filteredOptionsArr = profile.regions.filter(
            (region) => region.region_group === userRegion.region_group
          );
        }
        const reducedOptionsArr = [];
        filteredOptionsArr.forEach((region) => reducedOptionsArr.push(region.verbose_name));
        setRegionsOptions(reducedOptionsArr);
        setRegionGroupArr(groupArr);
      }
    }
    if (profile.departments?.length > 0 && profile.access !== '2') {
      const index = Number(profile.department) - 1;
      if (index > -1) {
        setActiveDept(profile.departments[index]);
        if (profile.sub_departments.length > 0 && profile.sub_department) {
          setActiveSubDept(profile.sub_departments[index][Number(profile.sub_department) - 1]);
          if (profile.department) {
            setSubDeptsArr(profile.sub_departments[index]);
          }
        }
      }
    }
  }, [profile]);

  // Removed button disabled logic - button should always be enabled

  const handleEmailChange = (event) => {
    let valid = false;
    const emailRegex =
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    valid = emailRegex.test(String(event.target.value).toLowerCase());

    setIsEmailValid(valid);

    // Clear email errors when user starts typing
    if (fieldErrors.emailRequired || fieldErrors.emailInvalid) {
      clearFieldError('emailRequired');
      clearFieldError('emailInvalid');
    }

    setProfileItem((prevState) => ({
      ...prevState,
      [event.target.id]: event.target.value,
    }));
  };

  const handleNameChange = (event) => {
    let valid = false;
    const nameRegex = /^[a-zA-Z0-9\s.'-]*$/;
    valid = nameRegex.test(event.target.value);
    valid = event.target.value.length < 50 && valid;

    // Clear name error when user starts typing
    if (fieldErrors.nameRequired) {
      clearFieldError('nameRequired');
    }

    if (valid) {
      setProfileItem((prevState) => ({
        ...prevState,
        [event.target.id]: event.target.value,
      }));
    }
  };

  const handleSelectDept = (event) => {
    const department = event;
    setActiveDept(department);
    if (subDeptsArr.length > 0) {
      const index = profileItem.departments.indexOf(department);
      setActiveSubDept(profileItem.sub_departments[index][0]);
      setSubDeptsArr(profileItem.sub_departments[index]);
    }
  };

  const handleSelectSubDept = (event) => {
    const subdept = event;
    setActiveSubDept(subdept);
  };

  const handleSelectRegion = (event) => {
    const region = event;
    setActiveRegion(region);
  };

  const handleSelectRegionGroup = (event) => {
    const group = event;
    setRegionGroup(group);
    const regionFilteredArr = allRegions.filter((region) => region.region_group === group);
    const reducedOptions = [];
    regionFilteredArr.map((region) => reducedOptions.push(region.verbose_name));
    setRegionsOptions(reducedOptions);
    setActiveRegion(reducedOptions[0]);
  };

  const saveProfile = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorSummary([]);
    setIsAfterSubmit(true);

    // Collect all validation errors first
    const errors = [];
    const newFieldErrors = { ...fieldErrors };

    // Check required fields
    if (!profileItem.name?.trim()) {
      errors.push('name');
      newFieldErrors.nameRequired = true;
    } else {
      newFieldErrors.nameRequired = false;
    }

    if (!profileItem.email?.trim()) {
      errors.push('email');
      newFieldErrors.emailRequired = true;
      newFieldErrors.emailInvalid = false; // Don't show invalid error if email is empty
    } else {
      newFieldErrors.emailRequired = false;

      if (!isEmailValid) {
        errors.push('email');
        newFieldErrors.emailInvalid = true;
      } else {
        newFieldErrors.emailInvalid = false;
      }
    }

    if (profileItem.regions?.length > 0 && !activeRegion) {
      errors.push('region');
      newFieldErrors.regionRequired = true;
    } else {
      newFieldErrors.regionRequired = false;
    }

    if (profileItem.departments?.length > 0 && auth.access !== '2' && !activeDept) {
      errors.push('department');
      newFieldErrors.departmentRequired = true;
    } else {
      newFieldErrors.departmentRequired = false;
    }

    if (
      profileItem.departments?.length > 0 &&
      auth.access !== '2' &&
      profileItem.sub_departments?.[activeDept]?.length > 0 &&
      !activeSubDept
    ) {
      errors.push('subDepartment');
      newFieldErrors.subDepartmentRequired = true;
    } else {
      newFieldErrors.subDepartmentRequired = false;
    }

    // Set all field errors at once
    setFieldErrors(newFieldErrors);

    // If there are validation errors, don't proceed
    if (errors.length > 0) {
      setErrorSummary(errors);
      return;
    }

    // Process department changes
    if (profileItem.departments?.length > 0 && auth.access !== '2') {
      const deptIndex = profileItem.departments.indexOf(activeDept);
      if (
        profileItem.department &&
        profileItem.departments[Number(profileItem.department) - 1] !== activeDept
      ) {
        profileItem.department = String(deptIndex + 1);
      } else if (!profileItem.department && activeDept) {
        // if departments were added after the user signed up, but now needs/can pick a department
        profileItem.department = String(deptIndex + 1);
      }
      if (activeSubDept) {
        profileItem.sub_department = String(
          profileItem.sub_departments[deptIndex].indexOf(activeSubDept) + 1
        );
      }
      // if user changed departments from one with sub-departments to one without, clear sub-department
      if (
        profileItem?.sub_departments &&
        activeSubDept === undefined &&
        (!profileItem.sub_departments?.[deptIndex] ||
          profileItem.sub_departments?.[deptIndex]?.length < 1)
      ) {
        profileItem.sub_department = '';
      }
    }

    // Process region changes
    if (profileItem.regions.length > 0) {
      const region = profileItem.regions.find((obj) => obj.verbose_name === activeRegion);
      if (profileItem.region !== region._id) {
        profileItem.region = region._id;
      }
    }

    if (profileItem && auth.token) {
      saveChanges(profileItem);
      showGreenCheckOverlay();
    }
  };
  return {
    modalIsOpen,
    modalTextContent,
    profile: profileItem,
    activeDept,
    activeSubDept,
    subDeptsArr,
    activeRegion,
    regionArr,
    regionGroupArr,
    regionGroup,
    regionsOptions,
    showGreenCheck,
    isEmailValid,
    modalClickHandler,
    handleEmailChange,
    handleNameChange,
    saveProfile,
    handleSelectRegionGroup,
    handleSelectRegion,
    handleSelectSubDept,
    handleSelectDept,
    // Error handling
    isAfterSubmit,
    errorSummary,
    fieldErrors,
    getFieldError,
    hasFieldError,
    clearFieldError,
  };
};
